/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api\index.ts
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the Provisioning Templates module.
────────────────────────────────────────────────────────────────*/

import { api } from '../../../lib/api';

export interface Vendor {
    id: number;
    uuid?: string;
    name: string;
}

export interface DeviceModel {
    id: number;
    uuid?: string;
    vendorId: number;
    vendor?: Vendor;
    model: string;
    accounts?: unknown;
    keys?: unknown;
    timezone?: unknown;
    language?: unknown;
    wallpaper?: boolean;
    timeFormat?: unknown;
    dateFormat?: unknown;
    expansionModuleKeys?: unknown;
    summerTime?: unknown;
    daylightSavings?: unknown;
    phonebook?: unknown;
    isSystem?: boolean;
    imageFile?: string;
    templateFile?: string;    // Path to base template file (extension determines format: .cfg, .xml, .j2, .txt)
}

export interface ServerSetting {
    id: number;
    uuid?: string;
    name: string;
    serverDomain: string;
}

export interface Template {
    id: number;
    uuid?: string;
    name: string;
    vendorId: number;
    modelId: number;
    serverSettingsId: number;
    domainId: number;
    domain?: {
        uuid: string;
    };

    // Configurations
    timezone?: string;
    language?: string;

    // JSON Data
    keys?: unknown[];
    expansionModules?: unknown[];
    phonebook?: unknown[];

    // GUI Settings
    adminPassword?: string;
    timeFormat?: string;
    dateFormat?: string;
    summerTime?: string;
    daylightSavings?: string;
    wallpaper?: string;

    // Custom - stores the customized template content
    customConfig?: string;

    // Relations
    vendor?: Vendor;
    model?: DeviceModel;
    serverSettings?: ServerSetting;
}

// API Functions
export const getTemplates = (domainId?: number) => {
    const params = domainId !== undefined ? `?domainId=${domainId}` : '';
    return api.get<Template[]>(`/api/apps/provisioning/templates${params}`).then(res => res.data);
};
export const getTemplate = (id: number | string) => api.get<Template>(`/api/apps/provisioning/templates/${id}`).then(res => res.data);
export const createTemplate = (data: Partial<Template>) => api.post<Template>('/api/apps/provisioning/templates', data).then(res => res.data);
export const updateTemplate = (id: number | string, data: Partial<Template>) => api.put<Template>(`/api/apps/provisioning/templates/${id}`, data).then(res => res.data);
export const deleteTemplate = (id: number | string) => api.delete(`/api/apps/provisioning/templates/${id}`);

// Also need these for the form
export const getVendors = () => api.get<Vendor[]>('/api/apps/provisioning/vendors').then(res => res.data);
export const getDeviceModels = () => api.get<DeviceModel[]>('/api/apps/provisioning/device-models').then(res => res.data);
export const getDeviceModel = (id: number | string) => api.get<DeviceModel>(`/api/apps/provisioning/device-models/${id}`).then(res => res.data);
export const getServerSettings = () => api.get<ServerSetting[]>('/api/apps/provisioning/server-settings').then(res => res.data);
